import { useMemo, useState } from "react";
import PageLayout from "../components/PageLayout";
import { ACHIEVEMENT_ICONS, DEFAULT_ACHIEVEMENT_ICON } from "../constants/achievementIcons";
import useAchievements from "../hooks/useAchievements";
import { useLens } from "../lens/LensProvider";
import { Achievement } from "../types/achievements";
import { earnedByLabel } from "../utils/pilotCount";
import { renderSafeDescription } from "../utils/safeDescription";

function formatEarnedDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { timeZone: "Australia/Melbourne", day: "numeric", month: "short", year: "numeric" });
}

// Locked achievements past this are folded away: the closest goals matter, not the whole catalogue.
const LOCKED_VISIBLE = 8;

const byEarnedDate = (a: Achievement, b: Achievement) => b.earned_at!.localeCompare(a.earned_at!) || a.name.localeCompare(b.name);
// The achievements more pilots have earned are the nearer goals.
const byCommonness = (a: Achievement, b: Achievement) => b.earned_count - a.earned_count || a.name.localeCompare(b.name);

function AchievementCard({ achievement, personal }: { achievement: Achievement; personal: boolean }) {
  const icon = ACHIEVEMENT_ICONS[achievement.id] ?? DEFAULT_ACHIEVEMENT_ICON;
  const earned = !!achievement.earned_at;
  const state = personal ? (earned ? " is-earned" : " is-locked") : "";

  return (
    <div className={`achievement-card${state}`}>
      <div className="achievement-icon" aria-hidden="true">{icon}</div>
      <div className="achievement-body">
        <h2 className="achievement-name">{achievement.name}</h2>
        <p className="achievement-description">{renderSafeDescription(achievement.description)}</p>
        {personal && (
          <span className="achievement-status">
            {earned ? <>Unlocked <time dateTime={achievement.earned_at!}>{formatEarnedDate(achievement.earned_at!)}</time></> : "Locked"}
          </span>
        )}
        {!personal && (
          <span className="achievement-rarity">{achievement.earned_count > 0 ? earnedByLabel(achievement.earned_count) : "Not yet earned"}</span>
        )}
      </div>
    </div>
  );
}

export default function AchievementsPage() {
  const { activeUid } = useLens();
  const { achievements, loading, error } = useAchievements(activeUid);
  const personal = !!activeUid;
  const [showAllLocked, setShowAllLocked] = useState(false);
  const { earned, locked } = useMemo(() => ({
    earned: achievements.filter(a => a.earned_at).sort(byEarnedDate),
    locked: achievements.filter(a => !a.earned_at).sort(byCommonness),
  }), [achievements]);
  const ordered = personal ? earned : [...achievements].sort(byCommonness);
  const lockedShown = showAllLocked ? locked : locked.slice(0, LOCKED_VISIBLE);
  const earnedTotal = earned.length;

  return (
    <PageLayout route="achievements" hero={{
      photo: "f15", focus: "50% 18%", title: "Achievements",
      body: "Awarded for standout performance and rare feats.",
    }}>

      <section className="panel panel-list">
        {loading && <p className="achievements-loading">Loading…</p>}
        {error && <p className="achievements-error">{error}</p>}
        {!loading && !error && personal && (
          <div className="achievement-progress">
            <div className="achievement-progress-text"><strong>{earnedTotal}</strong> / {achievements.length} unlocked</div>
            <div className="pilot-achievements-progress" role="progressbar" aria-label="Achievements unlocked"
              aria-valuemin={0} aria-valuemax={achievements.length} aria-valuenow={earnedTotal}>
              <div style={{ width: `${achievements.length ? (earnedTotal / achievements.length) * 100 : 0}%` }} />
            </div>
            {earnedTotal === 0 && <p className="achievement-progress-empty">Nothing unlocked yet. Here is what is available.</p>}
          </div>
        )}
        {!loading && !error && (
          <ul className="achievement-list">
            {[...ordered, ...(personal ? lockedShown : [])].map((a) => (
              <li key={a.id}>
                <AchievementCard achievement={a} personal={personal} />
              </li>
            ))}
          </ul>
        )}
        {!loading && !error && personal && locked.length > LOCKED_VISIBLE && (
          <button type="button" className="stats-section-more" aria-expanded={showAllLocked} onClick={() => setShowAllLocked(v => !v)}>
            {showAllLocked ? "Show fewer locked" : `Show ${locked.length - LOCKED_VISIBLE} more locked`}
          </button>
        )}
      </section>
    </PageLayout>
  );
}
